/**
 * CSRF Token Hook
 * Fetches and manages CSRF tokens for secure API requests
 */

import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';

export const useCsrfToken = () => {
  const [csrfToken, setCsrfToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCsrfToken = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use API_BASE_URL for production compatibility
      const baseUrl = API_BASE_URL || window.location.origin;
      const url = `${baseUrl}/api/csrf-token`;

      console.log('🔒 Fetching CSRF token from:', url);

      const response = await fetch(url, {
        credentials: 'include' // Important: include cookies for CSRF validation
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CSRF token: ${response.status}`);
      }

      const data = await response.json();

      if (!data.csrfToken) {
        throw new Error('CSRF token not found in response');
      }

      setCsrfToken(data.csrfToken);
      setError(null);

      console.log('✅ CSRF token fetched successfully');
    } catch (err) {
      console.error('❌ Error fetching CSRF token:', err);
      console.error('❌ Error details:', {
        message: err.message,
        url: `${API_BASE_URL || window.location.origin}/api/csrf-token`
      });
      setError(err.message);
      setCsrfToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch token on mount
  useEffect(() => {
    fetchCsrfToken();
  }, [fetchCsrfToken]);

  // Refetch token periodically (every 30 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('🔄 Refreshing CSRF token...');
      fetchCsrfToken();
    }, 30 * 60 * 1000); // 30 minutes

    return () => clearInterval(interval);
  }, [fetchCsrfToken]);

  return { 
    csrfToken, 
    loading, 
    error, 
    refetchToken: fetchCsrfToken 
  };
};

